import React, { useState } from 'react';
import { useCanvasStore } from '@/stores/canvasStore';
import { ViewModeSwitcher } from '@/components/navigation/ViewModeSwitcher';
import { UserMenu } from '@/components/auth/UserMenu';
import { Button } from '@/components/ui/button';
import { Sun, Moon, Bug } from 'lucide-react';
import { BugReportDialog } from '@/components/feedback/BugReportDialog';
import { cn } from '@/lib/utils';

interface TopBarProps {
  scrollOffset?: number;
}

export function TopBar({ scrollOffset = 0 }: TopBarProps) {
  const { isDarkMode, toggleDarkMode, viewMode } = useCanvasStore();
  const [showBugReport, setShowBugReport] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // In discover mode, the TopBar moves up as user scrolls
  // Use CSS transition for smooth movement, but clamp values to prevent sticking
  const isDiscover = viewMode === 'discover';
  const clampedOffset = Math.max(0, Math.min(scrollOffset, 100));
  const translateY = isDiscover ? Math.min(clampedOffset * 0.8, 80) : 0;
  const opacity = isDiscover ? Math.max(1 - (clampedOffset / 100), 0) : 1;

  return (
    <div 
      className={cn(
        "fixed left-1/2 z-50 top-5",
        // Use CSS transition for smoother animation
        "transition-[transform,opacity] duration-200 ease-out"
      )}
      style={{ 
        transform: `translateX(-50%) translateY(-${translateY}px)`,
        opacity,
        pointerEvents: opacity < 0.3 ? 'none' : 'auto'
      }}
    >
      {/* Floating Pill Navigation */}
      <div
        className="floating-pill rounded-full px-3 py-2 flex items-center gap-2 max-w-[600px]"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Bug Report */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setShowBugReport(true)}
          title="Report a bug"
          className="h-9 w-9 rounded-full"
        >
          <Bug className="h-4 w-4" />
        </Button>

        {/* View Mode Switcher */}
        <ViewModeSwitcher expanded={isHovered} />

        {/* Theme toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleDarkMode}
          className="h-9 w-9 rounded-full"
        >
          {isDarkMode ? (
            <Sun className="h-4 w-4" />
          ) : (
            <Moon className="h-4 w-4" />
          )}
        </Button>

        {/* Separator */}
        <div className="w-px h-6 bg-border/50" />

        {/* User Menu */}
        <UserMenu />
      </div>

      {/* Bug Report Dialog */}
      <BugReportDialog 
        isOpen={showBugReport} 
        onClose={() => setShowBugReport(false)} 
      />
    </div>
  );
}
